import { createApi } from "@reduxjs/toolkit/query/react";

import apiBaseQuery from "./axiosBaseQuery";
import authBaseQuery from "./authBaseQuery";
import { extractRoutesFromResponse } from "../../utils/RouteExtractor";
import { identifyFullStoryUser } from "../../utils/fullstory";
export const USERS_API_REDUCER_KEY = "usersApi";
export const NEW_USERS_API_REDUCER_KEY = "newUserApi";

export const usersApi = createApi({
  reducerPath: USERS_API_REDUCER_KEY,
  baseQuery: apiBaseQuery,
  tagTypes: ["user"],
  endpoints: (builder) => ({
    getMe: builder.query({
      query: () => ({
        url: "/admin/users/me/",
      }),
      providesTags: ["user"],
    }),

    getUsers: builder.query({
      query: ({ page, limit }) => ({
        url: `/admin/users/`,
        params: {
          page: page || 1,
          limit: limit || 100,
        },
      }),
      providesTags: ["user"],
    }),

    getUser: builder.query({
      query: (id) => {
        if (!id) {
          return;
        }
        return {
          url: `/admin/users/${id}/`,
        };
      },
      providesTags: ["user"],
    }),

    createUser: builder.mutation({
      query: (body) => {
        return {
          url: "/admin/users/",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),
    updateUser: builder.mutation({
      query: ({ id, ...rest }) => {
        return {
          url: `/admin/users/${id}/`,
          method: "PUT",
          body: rest,
        };
      },
      invalidatesTags: ["user"],
    }),
    deleteUser: builder.mutation({
      query: (id) => {
        return {
          url: `/admin/users/${id}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["user"],
    }),
    forgotPassword: builder.mutation({
      query: (body) => {
        return {
          url: `/admin/users/email-reset/`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),
    resetPassword: builder.mutation({
      query: (body) => {
        return {
          url: `/admin/users/reset-password`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),
  }),
});

export const newUserApi = createApi({
  reducerPath: NEW_USERS_API_REDUCER_KEY,
  baseQuery: authBaseQuery,
  tagTypes: ["user", "access"],
  endpoints: (builder) => ({
    getMe: builder.query({
      query: () => ({
        url: "/core/me",
      }),
      providesTags: ["user"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.id == null) return;
          identifyFullStoryUser(String(data.id), {
            displayName: `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim(),
            email: data.email,
          });
        } catch {
          // getMe failed (e.g. not logged in) — nothing to identify
        }
      },
    }),
    createNewUser: builder.mutation({
      query: (body) => {
        return {
          url: "/core/user/register",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),
    getAllUsers: builder.query({
      query: () => {
        return {
          url: "/core/users",
          method: "GET",
        };
      },
      invalidatesTags: ["user"],
    }),
    getNewUser: builder.query({
      query: (id) => {
        if (!id) {
          return;
        }
        return {
          url: `/core/users/${id}`,
        };
      },
    }),

    deleteNewUser: builder.mutation({
      query: (userID) => {
        return {
          url: `/core/user/delete/${userID}`,
          method: "DELETE",
        };
      },
    }),
    updateNewUser: builder.mutation({
      query: ({ userID, body }) => {
        return {
          url: `/core/user/edit/${userID}`,
          method: "PUT",
          body,
        };
      },
    }),
    updatepassword: builder.mutation({
      query: (body) => {
        return {
          url: "/core/password/update",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),

    forgotPasswordtomail: builder.mutation({
      query: (body) => {
        return {
          url: "/core/password/forgot",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),

    resetPasswordtomail: builder.mutation({
      query: (body) => {
        return {
          url: "/core/password/reset",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),

    Addrole: builder.mutation({
      query: (body) => {
        return {
          url: "/core/role-creation",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),

    Getrole: builder.query({
      query: () => {
        return {
          url: "/core/get-roles",
        };
      },
      invalidatesTags: ["user"],
    }),

    getPermissions: builder.query({
      query: (role) => {
        return {
          url: `/core/access/fetch`,
          params: { role },
        };
      },
      invalidatesTags: ["user"],
    }),

    updatePermissions: builder.mutation({
      query: (body) => {
        return {
          url: `/core/access/set`,
          method: "PATCH",
          body,
        };
      },
    }),

    createRole: builder.mutation({
      query: (body) => {
        return {
          url: "/core/role-creation",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["user"],
    }),

    updateRolePermission: builder.mutation({
      query: ({ role_id, permissions }) => {
        return {
          url: `/core/update-access/${role_id}`,
          method: "PATCH",
          body: permissions,
        };
      },
      invalidatesTags: ["users"],
    }),

    DeletRole: builder.mutation({
      query: (roleid) => {
        return {
          url: `/core/delete-roles/${roleid}`,
          method: "DELETE",
        };
      },
    }),
    getUsersActivity: builder.query({
      query: () => ({
        url: "/user_activity",
      }),
    }),
    fetchAccessModules: builder.query({
      query: () => ({
        url: "/core/fetch_access_elements",
      }),
      providesTags: ["access"],
      transformResponse: (response) => {
        const result = extractRoutesFromResponse(response);
        return { response, result };
      },
    }),
    getModules: builder.query({
      query: () => ({
        url: "/core/fetch_modules",
      }),
    }),
    setModules: builder.mutation({
      query: (body) => ({
        url: `/core/set_modules`,
        method: "POST",
        body,
      }),
    }),
  }),
});
export const {
  useCreateUserMutation,
  // useGetMeQuery,
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = usersApi;

export const {
  useCreateNewUserMutation,
  useDeleteNewUserMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useGetNewUserQuery,
  useUpdateNewUserMutation,
  useUpdatepasswordMutation,
  useResetPasswordtomailMutation,
  useAddroleMutation,
  useDeletRoleMutation,
  useGetroleQuery,
  useGetUsersActivityQuery,
  useGetPermissionsQuery,
  useUpdatePermissionsMutation,
  useForgotPasswordtomailMutation,
  useGetAllUsersQuery,
  useCreateRoleMutation,
  useUpdateRolePermissionMutation,
  useFetchAccessModulesQuery,
  useLazyFetchAccessModulesQuery,
  useGetModulesQuery,
  useSetModulesMutation,
} = newUserApi;
